import { useState } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CardContent from "@mui/material/CardContent";

import { colors } from "../../../theme/colors";
import style from "../surgeon.module.css";
import { ExpandMore } from "./ExpandMore";
import { formatAddressLine } from "./addressHelpers";

interface AddressDetailsProps {
  address?: AddressDetails;
  isDefault?: boolean;
  onUpdateDefaultAddress?: () => void;
  hideDefaultToggle?: boolean;
}

/**
 * Renders a surgeon delivery address with expand/collapse and default toggle controls.
 *
 * Args:
 *   address (AddressDetails | undefined): Address details to display.
 *   isDefault (boolean | undefined): Indicates if this address is the default delivery address.
 *   onUpdateDefaultAddress (() => void | undefined): Callback invoked to set this address as default.
 *   hideDefaultToggle (boolean | undefined): When true, hides the default toggle UI.
 *
 * Notes:
 *   Persistence and validation are handled by the parent; this component is presentational only.
 */
const AddressDetails = ({
  address,
  isDefault,
  onUpdateDefaultAddress,
  hideDefaultToggle,
}: AddressDetailsProps) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Box
      sx={{
        border: `1px solid ${colors.grey}`,
        borderRadius: "4px",
        padding: "7px",
        marginBottom: "10px",
      }}
    >
      <Box
        className={style.addressRowContainer}
        sx={{
          flexDirection: "row",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            flexDirection: "row",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <ExpandMore
            expand={expanded}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="show more"
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ExpandMore>
          <Typography className={style.addressLabel} color={colors.black}>
            {formatAddressLine(address)}
          </Typography>
        </Box>
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          {/* Intent: default selection is persisted by parent to avoid stale cache; super admin can hide toggle. */}
          {!hideDefaultToggle && (
            <Box
              className={style.defaultContainer}
              sx={{
                backgroundColor: colors.lightGreen,
                border: `1px solid ${colors.darkGreen}`,
                borderRadius: "4px",
                marginRight: "10px",
                display: "flex",
                flexDirection: "row",
                cursor: "pointer",
              }}
              onClick={() => {
                if (!isDefault) {
                  if (onUpdateDefaultAddress) {
                    onUpdateDefaultAddress();
                  }
                }
              }}
            >
              <Typography
                className={style.cell}
                sx={{ fontSize: "12px", color: colors.darkGreen }}
              >
                {isDefault ? "Default" : "Set As Default"}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Collapse
        in={expanded}
        timeout="auto"
        unmountOnExit
        sx={{ marginLeft: "25px" }}
      >
        <CardContent sx={{ marginTop: "-25px" }}>
          {address?.mail && (
            <Box
              className={style.addressRowContainer}
              sx={{
                flexDirection: "row",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <Typography className={style.addressLabel} color={colors.black}>
                Email:
              </Typography>
              <Typography
                className={style.addressValue}
                color={colors.black}
                sx={{ marginLeft: "5px" }}
              >
                {address?.mail}
              </Typography>
            </Box>
          )}
          {address?.phone && (
            <Box
              className={style.addressRowContainer}
              sx={{
                flexDirection: "row",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <Typography className={style.addressLabel} color={colors.black}>
                Tel:
              </Typography>
              <Typography
                className={style.addressValue}
                color={colors.black}
                sx={{ marginLeft: "5px" }}
              >
                {address?.phone}
              </Typography>
            </Box>
          )}
          {address?.fax && (
            <Box
              className={style.addressRowContainer}
              sx={{
                flexDirection: "row",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <Typography className={style.addressLabel} color={colors.black}>
                Fax:
              </Typography>
              <Typography
                className={style.addressValue}
                color={colors.black}
                sx={{ marginLeft: "5px" }}
              >
                {address?.fax}
              </Typography>
            </Box>
          )}
          {address?.provider_number && (
            <Box
              className={style.addressRowContainer}
              sx={{
                flexDirection: "row",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <Typography className={style.addressLabel} color={colors.black}>
                Provider No:
              </Typography>
              <Typography
                className={style.addressValue}
                color={colors.black}
                sx={{ marginLeft: "5px" }}
              >
                {address?.provider_number}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Collapse>
    </Box>
  );
};

export default AddressDetails;
